import { ArrowLeftRight, Calendar } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	type ColumnConfig,
	type ColumnFilters,
	MaximusTableReusable,
} from "@/components/MaximusTableReusable";
import type { ScreenName } from "@/constants/screenNames";
import { useToast } from "@/hooks/use-toast";
import { useExportToExcel } from "@/hooks/useExportToExcel";
import { usePermissions } from "@/hooks/usePermissions";
import {
	type DefaultColumn,
	useScreenColumnPreferences,
} from "@/hooks/useScreenColumnPreferences";
import { useTerminology } from "@/hooks/useTerminology";
import { cn, formatDate, formatDateOnlyLocal } from "@/lib/utils";
import { useApp } from "../../contexts/AppContext";
import { useFormatting } from "../../hooks/useFormatting";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

export type ViewMode = "balances" | "transactions";

/** Identifier for each Transactions quick-filter button. */
export type QuickFilterKey = "7" | "30" | "90";

/** Inclusive `forecastDate` window (yyyy-MM-dd) for a single quick filter. */
export interface QuickFilterRange {
	ge: string;
	le: string;
}

/** The 7/30/90-day windows built from the forecast start date. */
export type QuickFilterRanges = Record<QuickFilterKey, QuickFilterRange>;

/** Ordered keys / labels for rendering the quick-filter buttons. */
const QUICK_FILTER_KEYS: QuickFilterKey[] = ["7", "30", "90"];
const QUICK_FILTER_LABELS: Record<QuickFilterKey, string> = {
	"7": "7d",
	"30": "30d",
	"90": "90d",
};
const DEFAULT_QUICK_FILTER: QuickFilterKey = "90";

/**
 * Parse a `yyyy-MM-dd` string into a Date at local midnight. Using the local
 * constructor (rather than `new Date(iso)`, which parses as UTC) keeps the
 * round-trip through `formatDateOnlyLocal` stable regardless of timezone, so
 * the visible filter and the derived API filter agree exactly.
 */
function parseIsoDateLocal(iso: string): Date {
	const [year, month, day] = iso.split("-").map(Number);
	return new Date(year, month - 1, day);
}

/** Build the `forecastDate` date-range column filter for a quick-filter range. */
function quickFilterColumnFilters(range: QuickFilterRange): ColumnFilters {
	return {
		forecastDate: {
			dateFrom: parseIsoDateLocal(range.ge),
			dateTo: parseIsoDateLocal(range.le),
		},
	};
}

/** True when the applied `forecastDate` filter matches the given range. */
function forecastDateMatchesRange(
	filters: ColumnFilters,
	range: QuickFilterRange,
): boolean {
	const forecastDate = filters.forecastDate;
	if (!forecastDate?.dateFrom || !forecastDate?.dateTo) return false;
	return (
		formatDateOnlyLocal(forecastDate.dateFrom) === range.ge &&
		formatDateOnlyLocal(forecastDate.dateTo) === range.le
	);
}

/** Unified display row covering both view modes. */
export interface ForecastRow {
	rowKey: string;
	id: number;
	// transactions
	forecastDate?: string | null;
	dueDate?: string | null;
	counterpartyName?: string | null;
	description?: string | null;
	category?: string | null;
	direction?: string | null;
	type?: string | null;
	grossAmount?: number | null;
	outstandingAmount?: number | null;
	layer?: string | null;
	status?: string | null;
	reference?: string | null;
	dataSourceName?: string | null;
	// balances
	date?: string | null;
	openingBalance?: number | null;
	balance?: number | null;
	incoming?: number | null;
	outgoing?: number | null;
	confidenceScore?: number | null;
	contributingLayers?: string | null;
}

export interface SortInput {
	field: string;
	order: "Ascending" | "Descending";
}

/** Structural subset of a transaction node returned by either backing view. */
export interface TransactionNode {
	id: number;
	forecastDate: string | null;
	dueDate: string | null;
	counterpartyName: string | null;
	description: string | null;
	category: string | null;
	direction: string | null;
	type: string | null;
	grossAmount: number | null;
	outstandingAmount: number | null;
	layer: string | null;
	status: string | null;
	reference: string | null;
	dataSourceName: string | null;
}

/** Structural subset of a data-point (balance) node returned by either view. */
export interface BalanceNode {
	id: number;
	date: string | null;
	openingBalance: number | null;
	balance: number | null;
	incoming: number | null;
	outgoing: number | null;
	confidenceScore: number | null;
	contributingLayers: string | null;
}

interface Connection<T> {
	edges: { cursor: string; node: T }[];
	pageInfo: {
		hasNextPage: boolean;
		hasPreviousPage: boolean;
		startCursor: string | null;
		endCursor: string | null;
	};
}

export interface TransactionOptions {
	category: string[];
	direction: string[];
	type: string[];
	layer: string[];
	status: string[];
	dataSource: string[];
}

/**
 * A data source for {@link TransactionsBalancesTable}. Everything that differs
 * between the base 90-day forecast table and the scenario table is provided
 * here, so the table body itself stays identical across both.
 */
export interface TransactionsTableSource {
	title: string;
	screenName: ScreenName;
	/** Export form names passed to useExportToExcel (live mode only). */
	exportTransactionsForm: string;
	exportBalancesForm: string;
	exportTransactionsFileName: string;
	exportBalancesFileName: string;
	fetchTransactions: (
		filter: Record<string, unknown> | undefined,
		first: number,
		after: string | undefined,
		sort: SortInput[] | undefined,
	) => Promise<Connection<TransactionNode>>;
	fetchTransactionsCount: (
		filter: Record<string, unknown> | undefined,
	) => Promise<number>;
	fetchDataPoints: (
		filter: Record<string, unknown> | undefined,
		first: number,
		after: string | undefined,
		sort: SortInput[] | undefined,
	) => Promise<Connection<BalanceNode>>;
	fetchDataPointsCount: (
		filter: Record<string, unknown> | undefined,
	) => Promise<number>;
	fetchDistinctValues: (field: string) => Promise<string[]>;
	getDemoTransactionRows: () => ForecastRow[];
	getDemoBalanceRows: () => ForecastRow[];
	getDemoTransactionOptions: () => TransactionOptions;
	/**
	 * Horizon constraint merged into every live query for the given view mode.
	 * Return an empty object to apply no horizon constraint.
	 */
	horizonFilter: (mode: ViewMode) => Record<string, unknown>;
	/**
	 * A stable string key describing the current horizon anchor, used only as an
	 * effect dependency so live queries refetch when the anchor changes.
	 */
	horizonKey: string | null;
}

/**
 * ContributingLayers is stored as a JSON array of CffLayer enum names, e.g.
 * `["Known","CommittedOrders"]`. Render it as a human-readable, comma-separated
 * list with PascalCase names split into words (→ "Known, Committed Orders").
 */
function formatContributingLayers(raw: string | null | undefined): string {
	if (!raw) return "—";
	let names: string[];
	try {
		const parsed: unknown = JSON.parse(raw);
		names = Array.isArray(parsed)
			? parsed.map((v) => String(v))
			: [String(raw)];
	} catch {
		names = raw
			.replace(/[[\]"]/g, "")
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
	}
	const labels = names
		.map((name) => name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim())
		.filter(Boolean);
	return labels.length > 0 ? labels.join(", ") : "—";
}

/**
 * Convert a raw DB value into a human-readable label: split on underscores and
 * whitespace, then capitalise each word (`credit_note` → "Credit Note",
 * `outstanding` → "Outstanding"). Null/empty values fall back to the em-dash.
 */
function formatLabel(raw: string | null | undefined): string {
	if (!raw) return "—";
	const words = raw
		.split(/[_\s]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
	return words.length > 0 ? words.join(" ") : "—";
}

/** Map a status value to a Badge variant, matched case-insensitively. */
function statusBadgeVariant(
	status: string,
): "success" | "warning" | "destructive" | "secondary" | "info" {
	switch (status.toLowerCase()) {
		case "settled":
			return "success";
		case "outstanding":
			return "warning";
		case "overdue":
			return "destructive";
		case "unknown":
			return "secondary";
		default:
			return "info";
	}
}

/**
 * Default sort for the transactions view: Forecast Date ascending. Applied on
 * first load (live mode, server-side) and re-applied when switching back to the
 * transactions view. The balances view has no default sort.
 */
export const TRANSACTION_DEFAULT_SORT: SortInput[] = [
	{ field: "ForecastDate", order: "Ascending" },
];

const TRANSACTION_SORT_FIELD_MAP: Record<string, string> = {
	forecastDate: "ForecastDate",
	dueDate: "DueDate",
	counterpartyName: "CounterpartyName",
	description: "Description",
	category: "Category",
	direction: "Direction",
	type: "Type",
	grossAmount: "GrossAmount",
	outstandingAmount: "OutstandingAmount",
	layer: "Layer",
	status: "Status",
	reference: "Reference",
	dataSourceName: "DataSourceName",
};

const BALANCE_SORT_FIELD_MAP: Record<string, string> = {
	date: "Date",
	openingBalance: "OpeningBalance",
	balance: "Balance",
	incoming: "Incoming",
	outgoing: "Outgoing",
	confidenceScore: "ConfidenceScore",
	contributingLayers: "ContributingLayers",
};

const TRANSACTION_TEXT_FIELDS = new Set([
	"counterpartyName",
	"description",
	"category",
	"direction",
	"type",
	"layer",
	"status",
	"reference",
]);
const TRANSACTION_MULTI_SELECT_FIELDS = new Set(["dataSourceName"]);
const TRANSACTION_DATE_FIELDS = new Set(["forecastDate", "dueDate"]);
const TRANSACTION_NUMBER_FIELDS = new Set(["grossAmount", "outstandingAmount"]);

const BALANCE_TEXT_FIELDS = new Set(["contributingLayers"]);
const BALANCE_DATE_FIELDS = new Set(["date"]);
const BALANCE_NUMBER_FIELDS = new Set([
	"openingBalance",
	"balance",
	"incoming",
	"outgoing",
	"confidenceScore",
]);

const TRANSACTION_EXPORT_COLUMNS: { name: string; displayName: string }[] = [
	{ name: "counterpartyname", displayName: "Counterparty" },
	{ name: "description", displayName: "Description" },
	{ name: "category", displayName: "Category" },
	{ name: "direction", displayName: "Direction" },
	{ name: "type", displayName: "Type" },
	{ name: "grossamount", displayName: "Gross Amount" },
	{ name: "outstandingamount", displayName: "Outstanding Amount" },
	{ name: "forecastdate", displayName: "Forecast Date" },
	{ name: "duedate", displayName: "Due Date" },
	{ name: "layer", displayName: "Layer" },
	{ name: "status", displayName: "Status" },
	{ name: "reference", displayName: "Reference" },
	{ name: "datasourcename", displayName: "Data Source" },
];

const BALANCE_EXPORT_COLUMNS: { name: string; displayName: string }[] = [
	{ name: "date", displayName: "Date" },
	{ name: "openingbalance", displayName: "Opening Balance" },
	{ name: "balance", displayName: "Closing Balance" },
	{ name: "incoming", displayName: "Incoming" },
	{ name: "outgoing", displayName: "Outgoing" },
	{ name: "confidencescore", displayName: "Confidence Score" },
	{ name: "contributinglayers", displayName: "Contributing Layers" },
];

export const TRANSACTION_DEFAULT_COLUMNS: DefaultColumn[] = [
	{ key: "forecastDate", defaultVisible: true, defaultOrder: 0 },
	{ key: "counterpartyName", defaultVisible: true, defaultOrder: 1 },
	{ key: "description", defaultVisible: true, defaultOrder: 2 },
	{ key: "category", defaultVisible: true, defaultOrder: 3 },
	{ key: "direction", defaultVisible: true, defaultOrder: 4 },
	{ key: "type", defaultVisible: true, defaultOrder: 5 },
	{ key: "grossAmount", defaultVisible: true, defaultOrder: 6 },
	{ key: "outstandingAmount", defaultVisible: true, defaultOrder: 7 },
	{ key: "dueDate", defaultVisible: true, defaultOrder: 8 },
	{ key: "layer", defaultVisible: true, defaultOrder: 9 },
	{ key: "status", defaultVisible: true, defaultOrder: 10 },
	{ key: "reference", defaultVisible: false, defaultOrder: 11 },
	{ key: "dataSourceName", defaultVisible: true, defaultOrder: 12 },
];

export const BALANCE_DEFAULT_COLUMNS: DefaultColumn[] = [
	{ key: "date", defaultVisible: true, defaultOrder: 0 },
	{ key: "openingBalance", defaultVisible: true, defaultOrder: 1 },
	{ key: "balance", defaultVisible: true, defaultOrder: 2 },
	{ key: "incoming", defaultVisible: true, defaultOrder: 3 },
	{ key: "outgoing", defaultVisible: true, defaultOrder: 4 },
	{ key: "confidenceScore", defaultVisible: true, defaultOrder: 5 },
	{ key: "contributingLayers", defaultVisible: true, defaultOrder: 6 },
];

function buildApiFilter(
	columnFilters: ColumnFilters,
	textFields: Set<string>,
	dateFields: Set<string>,
	numberFields: Set<string>,
	multiSelectFields: Set<string> = new Set(),
): Record<string, unknown> | undefined {
	const entries = Object.entries(columnFilters);
	if (entries.length === 0) return undefined;

	const filterInput: Record<string, unknown> = {};

	for (const [columnKey, filterValue] of entries) {
		if (multiSelectFields.has(columnKey)) {
			if (filterValue.mode === "isEmpty") {
				filterInput[columnKey] = { isBlank: true };
			} else if (
				Array.isArray(filterValue.value) &&
				filterValue.value.length > 0
			) {
				filterInput[columnKey] = {
					containsValues: filterValue.value.map((v) => String(v)),
				};
			}
		} else if (textFields.has(columnKey)) {
			if (filterValue.mode === "isEmpty") {
				filterInput[columnKey] = { isBlank: true };
			} else if (filterValue.value != null && filterValue.value !== "") {
				const strValue = String(filterValue.value);
				filterInput[columnKey] =
					filterValue.mode === "equals"
						? { eq: strValue }
						: { contains: strValue };
			}
		} else if (dateFields.has(columnKey)) {
			const dateFilter: Record<string, string> = {};
			if (filterValue.dateFrom) {
				dateFilter.ge = formatDateOnlyLocal(filterValue.dateFrom);
			}
			if (filterValue.dateTo) {
				dateFilter.le = formatDateOnlyLocal(filterValue.dateTo);
			}
			if (Object.keys(dateFilter).length > 0) {
				filterInput[columnKey] = dateFilter;
			}
		} else if (numberFields.has(columnKey)) {
			const numFilter: Record<string, number> = {};
			if (filterValue.numberFrom != null) {
				numFilter.ge = filterValue.numberFrom;
			}
			if (filterValue.numberTo != null) {
				numFilter.le = filterValue.numberTo;
			}
			if (Object.keys(numFilter).length > 0) {
				filterInput[columnKey] = numFilter;
			}
		}
	}

	return Object.keys(filterInput).length > 0 ? filterInput : undefined;
}

function buildApiFilterForViewMode(
	viewMode: ViewMode,
	columnFilters: ColumnFilters | undefined,
): Record<string, unknown> | undefined {
	if (!columnFilters) return undefined;
	return viewMode === "transactions"
		? buildApiFilter(
				columnFilters,
				TRANSACTION_TEXT_FIELDS,
				TRANSACTION_DATE_FIELDS,
				TRANSACTION_NUMBER_FIELDS,
				TRANSACTION_MULTI_SELECT_FIELDS,
			)
		: buildApiFilter(
				columnFilters,
				BALANCE_TEXT_FIELDS,
				BALANCE_DATE_FIELDS,
				BALANCE_NUMBER_FIELDS,
			);
}

export interface TransactionsBalancesTableProps {
	source: TransactionsTableSource;
	/**
	 * General seam for opening the table pre-filtered (e.g. navigation from a
	 * dashboard/other page). Passed straight through to MaximusTableReusable as
	 * the visible filter seed and applied to the live query. Pass a
	 * stable/memoized object so it only re-seeds on an intentional change.
	 */
	initialColumnFilters?: ColumnFilters;
	/**
	 * Convenience for the same-page "View Transactions" click on the chart: when
	 * set, forces Transactions view and constrains the table to that single
	 * forecast day.
	 */
	dateFilter?: Date | null;
	/**
	 * Precomputed 7/30/90-day `forecastDate` windows for the Transactions
	 * quick-filter buttons. When omitted the quick filters are not rendered.
	 */
	quickFilterRanges?: QuickFilterRanges;
	/**
	 * Monotonic counter that forces a live-mode refetch of rows + count while
	 * preserving the current filters, sort, search and paging. Bump it whenever
	 * the underlying data has changed server-side (e.g. a scenario adjustment
	 * was saved or the scenario was refreshed/re-materialised).
	 */
	refreshSignal?: number;
}

/**
 * Shared Transactions/Balances table body used by both the base 90-day forecast
 * table and the scenario transactions table. All source-specific behaviour is
 * supplied via {@link TransactionsTableSource}.
 */
export const TransactionsBalancesTable: React.FC<
	TransactionsBalancesTableProps
> = ({
	source,
	initialColumnFilters,
	dateFilter,
	quickFilterRanges,
	refreshSignal,
}) => {
	const { hasPermission } = usePermissions();
	const { formatCurrency } = useFormatting();
	const { toast } = useToast();
	const { mode } = useApp();
	const isDemo = mode === "demo";
	const { cashMovementTerms: cashTerms, getForecastTerm } = useTerminology();

	const [viewMode, setViewMode] = useState<ViewMode>("transactions");
	const isTransactions = viewMode === "transactions";

	// A navigation seed (dashboard `initialColumnFilters` or chart `dateFilter`
	// drill-down) takes precedence over the default 90-day quick filter, so we
	// only seed the default quick filter when no navigation seed is present.
	const hasNavigationSeed = Boolean(dateFilter || initialColumnFilters);
	const defaultQuickFilterActive = Boolean(
		quickFilterRanges && !hasNavigationSeed,
	);
	const defaultQuickFilterColumnFilters = defaultQuickFilterActive
		? quickFilterColumnFilters(
				(quickFilterRanges as QuickFilterRanges)[DEFAULT_QUICK_FILTER],
			)
		: undefined;

	// Which quick filter (if any) is currently applied. Defaults to 90d on mount
	// so the initial Transactions view matches the existing 90-day horizon.
	const [activeQuickFilter, setActiveQuickFilter] =
		useState<QuickFilterKey | null>(
			defaultQuickFilterActive ? DEFAULT_QUICK_FILTER : null,
		);

	const [columnFiltersByView, setColumnFiltersByView] = useState<
		Partial<Record<ViewMode, ColumnFilters>>
	>(() =>
		defaultQuickFilterColumnFilters
			? { transactions: defaultQuickFilterColumnFilters }
			: {},
	);

	// Navigation filter seed for the table. A same-page `dateFilter`
	// (chart "View Transactions") collapses to a single-day forecastDate range;
	// otherwise the general `initialColumnFilters` prop is used directly
	// (dashboard/other-page navigation).
	const navigationColumnFilters = useMemo<ColumnFilters | undefined>(() => {
		if (dateFilter) {
			return { forecastDate: { dateFrom: dateFilter, dateTo: dateFilter } };
		}
		return initialColumnFilters;
	}, [dateFilter, initialColumnFilters]);

	// Navigation seeds only apply to Transactions. Otherwise use the last
	// user-applied filters for the current mode. Each retained object is the raw
	// object supplied by MaximusTableReusable, so its reference remains stable
	// until the user intentionally changes that mode's filters.
	const tableInitialColumnFilters = useMemo<ColumnFilters | undefined>(() => {
		if (isTransactions && navigationColumnFilters) {
			return navigationColumnFilters;
		}
		return columnFiltersByView[viewMode];
	}, [isTransactions, navigationColumnFilters, columnFiltersByView, viewMode]);

	// Demo mode: all rows are served in-memory and MaximusTableReusable handles
	// search / filter / sort / paging client-side.
	const demoRows = useMemo(
		() =>
			isDemo
				? isTransactions
					? source.getDemoTransactionRows()
					: source.getDemoBalanceRows()
				: [],
		[isDemo, isTransactions, source],
	);

	const [rows, setRows] = useState<ForecastRow[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [totalCount, setTotalCount] = useState(0);

	// Monotonic id so only the most recent row request updates state. This
	// prevents a stale/out-of-order response (e.g. from the previous view mode)
	// from clobbering the current results when toggling Balances/Transactions.
	const rowsRequestId = useRef(0);

	const [serverSearchTerm, setServerSearchTerm] = useState<string>("");
	const [serverPage, setServerPage] = useState(1);
	const [serverPageSize, setServerPageSize] = useState(25);
	const [pageCursors, setPageCursors] = useState<Record<number, string>>({});
	const [sortParams, setSortParams] = useState<SortInput[] | undefined>(
		isTransactions ? TRANSACTION_DEFAULT_SORT : undefined,
	);
	const [columnFilterParams, setColumnFilterParams] = useState<
		Record<string, unknown> | undefined
	>(() =>
		defaultQuickFilterColumnFilters
			? buildApiFilterForViewMode(
					"transactions",
					defaultQuickFilterColumnFilters,
				)
			: undefined,
	);

	// Distinct dropdown options (transactions mode only)
	const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
	const [directionOptions, setDirectionOptions] = useState<string[]>([]);
	const [typeOptions, setTypeOptions] = useState<string[]>([]);
	const [layerOptions, setLayerOptions] = useState<string[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>([]);
	const [dataSourceOptions, setDataSourceOptions] = useState<string[]>([]);

	const defaultColumns = isTransactions
		? TRANSACTION_DEFAULT_COLUMNS
		: BALANCE_DEFAULT_COLUMNS;
	const {
		visibleColumns: savedVisibleColumns,
		savePreferences: saveColumnPreferences,
	} = useScreenColumnPreferences(source.screenName, defaultColumns);

	// ── Data fetching ──────────────────────────────────────────────────────
	const fetchRows = useCallback(
		async (
			viewModeArg: ViewMode,
			sort: SortInput[] | undefined,
			first: number,
			after: string | undefined,
			filter: Record<string, unknown> | undefined,
		) => {
			const requestId = ++rowsRequestId.current;
			setIsLoading(true);
			setError(null);
			try {
				if (viewModeArg === "transactions") {
					const result = await source.fetchTransactions(
						filter,
						first,
						after,
						sort,
					);
					if (requestId !== rowsRequestId.current) return null;
					const mapped = result.edges.map((edge) => {
						const t = edge.node;
						return {
							rowKey: `txn-${t.id}-${edge.cursor}`,
							id: t.id,
							forecastDate: t.forecastDate,
							dueDate: t.dueDate,
							counterpartyName: t.counterpartyName,
							description: t.description,
							category: t.category,
							direction: t.direction,
							type: t.type,
							grossAmount: t.grossAmount,
							outstandingAmount: t.outstandingAmount,
							layer: t.layer,
							status: t.status,
							reference: t.reference,
							dataSourceName: t.dataSourceName,
						} as ForecastRow;
					});
					setRows(mapped);
					return result.pageInfo;
				}
				const result = await source.fetchDataPoints(filter, first, after, sort);
				if (requestId !== rowsRequestId.current) return null;
				const mapped = result.edges.map((edge) => {
					const d = edge.node;
					return {
						rowKey: `bal-${d.id}-${edge.cursor}`,
						id: d.id,
						date: d.date,
						openingBalance: d.openingBalance,
						balance: d.balance,
						incoming: d.incoming,
						outgoing: d.outgoing,
						confidenceScore: d.confidenceScore,
						contributingLayers: d.contributingLayers,
					} as ForecastRow;
				});
				setRows(mapped);
				return result.pageInfo;
			} catch (err) {
				if (requestId !== rowsRequestId.current) return null;
				console.error("Failed to fetch forecast rows:", err);
				setError("Failed to load forecast data");
				return null;
			} finally {
				if (requestId === rowsRequestId.current) setIsLoading(false);
			}
		},
		[source],
	);

	const fetchCount = useCallback(
		async (
			viewModeArg: ViewMode,
			filter: Record<string, unknown> | undefined,
		) => {
			try {
				const count =
					viewModeArg === "transactions"
						? await source.fetchTransactionsCount(filter)
						: await source.fetchDataPointsCount(filter);
				setTotalCount(count);
			} catch (err) {
				console.error("Failed to fetch forecast count:", err);
			}
		},
		[source],
	);

	// Fetch rows whenever query inputs change (live mode only — demo is in-memory)
	useEffect(() => {
		if (isDemo) return;
		const searchFilter = serverSearchTerm.trim()
			? { summary: serverSearchTerm.trim() }
			: undefined;
		const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
		const currentFetchPage = serverPage;
		const mergedFilter = {
			...source.horizonFilter(viewMode),
			...columnFilterParams,
			...searchFilter,
		};
		fetchRows(viewMode, sortParams, serverPageSize, cursor, mergedFilter).then(
			(pageInfo) => {
				if (pageInfo?.endCursor) {
					setPageCursors((prev) => ({
						...prev,
						[currentFetchPage + 1]: pageInfo.endCursor as string,
					}));
				}
			},
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		viewMode,
		sortParams,
		serverPage,
		serverPageSize,
		columnFilterParams,
		serverSearchTerm,
		source.horizonKey,
		refreshSignal,
	]);

	// Fetch total count whenever filters/search change (live mode only)
	useEffect(() => {
		if (isDemo) return;
		const searchFilter = serverSearchTerm.trim()
			? { summary: serverSearchTerm.trim() }
			: undefined;
		fetchCount(viewMode, {
			...source.horizonFilter(viewMode),
			...columnFilterParams,
			...searchFilter,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		viewMode,
		columnFilterParams,
		serverSearchTerm,
		source.horizonKey,
		refreshSignal,
	]);

	// Fetch distinct dropdown options for transactions mode
	useEffect(() => {
		if (!isTransactions) return;
		if (isDemo) {
			const opts = source.getDemoTransactionOptions();
			setCategoryOptions(opts.category);
			setDirectionOptions(opts.direction);
			setTypeOptions(opts.type);
			setLayerOptions(opts.layer);
			setStatusOptions(opts.status);
			setDataSourceOptions(opts.dataSource);
			return;
		}
		Promise.all([
			source.fetchDistinctValues("Category"),
			source.fetchDistinctValues("Direction"),
			source.fetchDistinctValues("Type"),
			source.fetchDistinctValues("Layer"),
			source.fetchDistinctValues("Status"),
			source.fetchDistinctValues("DataSourceName"),
		])
			.then(([categories, directions, types, layers, statuses, dataSources]) => {
				setCategoryOptions(categories);
				setDirectionOptions(directions);
				setTypeOptions(types);
				setLayerOptions(layers);
				setStatusOptions(statuses);
				setDataSourceOptions(dataSources);
			})
			.catch((err) => {
				console.error("Failed to fetch forecast filter options:", err);
			});
	}, [isTransactions, isDemo, source]);

	// ── Handlers ────────────────────────────────────────────────────────────
	const resetPaging = useCallback(() => {
		setServerPage(1);
		setPageCursors({});
	}, []);

	const handleViewModeChange = useCallback(
		(nextMode: ViewMode) => {
			if (nextMode === viewMode) return;
			setViewMode(nextMode);
			setRows([]);
			setSortParams(
				nextMode === "transactions" ? TRANSACTION_DEFAULT_SORT : undefined,
			);
			const nextColumnFilters =
				nextMode === "transactions" && navigationColumnFilters
					? navigationColumnFilters
					: columnFiltersByView[nextMode];
			setColumnFilterParams(
				buildApiFilterForViewMode(nextMode, nextColumnFilters),
			);
			setServerSearchTerm("");
			resetPaging();
		},
		[viewMode, navigationColumnFilters, columnFiltersByView, resetPaging],
	);

	const handleSearchChange = useCallback(
		(query: string) => {
			setServerSearchTerm(query);
			resetPaging();
		},
		[resetPaging],
	);

	const handleColumnFilterChange = useCallback(
		(filters: ColumnFilters) => {
			setColumnFiltersByView((current) => ({
				...current,
				[viewMode]: filters,
			}));
			setColumnFilterParams(buildApiFilterForViewMode(viewMode, filters));
			resetPaging();
			// A manual edit/clear of the forecastDate filter means the applied quick
			// filter no longer matches — drop the teal/active state without touching
			// the user's filter.
			if (activeQuickFilter && quickFilterRanges) {
				const stillMatches = forecastDateMatchesRange(
					filters,
					quickFilterRanges[activeQuickFilter],
				);
				if (!stillMatches) setActiveQuickFilter(null);
			}
		},
		[viewMode, resetPaging, activeQuickFilter, quickFilterRanges],
	);

	// Apply / toggle-off a 7/30/90-day quick filter. Applying merges its
	// forecastDate range into the current view's column filters (both the visible
	// seed and the live query); clicking the active one clears the forecastDate
	// filter entirely and deactivates the button.
	const handleQuickFilterClick = useCallback(
		(key: QuickFilterKey) => {
			if (!quickFilterRanges) return;
			const current = columnFiltersByView[viewMode] ?? {};
			let nextFilters: ColumnFilters;
			let nextActive: QuickFilterKey | null;
			if (key === activeQuickFilter) {
				const rest: ColumnFilters = { ...current };
				delete rest.forecastDate;
				nextFilters = rest;
				nextActive = null;
			} else {
				nextFilters = {
					...current,
					...quickFilterColumnFilters(quickFilterRanges[key]),
				};
				nextActive = key;
			}
			setActiveQuickFilter(nextActive);
			setColumnFiltersByView((currentByView) => ({
				...currentByView,
				[viewMode]: nextFilters,
			}));
			setColumnFilterParams(buildApiFilterForViewMode(viewMode, nextFilters));
			resetPaging();
		},
		[
			quickFilterRanges,
			activeQuickFilter,
			viewMode,
			columnFiltersByView,
			resetPaging,
		],
	);

	// Apply the seeded filter (from the chart `dateFilter` drill-down or a
	// fresh-mount `initialColumnFilters` navigation) to the *live query*. The
	// visible filter UI is handled separately by seeding MaximusTableReusable
	// with `navigationColumnFilters`; this effect keeps `columnFilterParams` (the
	// GraphQL query filter) in sync so live-mode fetch returns only the seeded
	// rows.
	//
	// Sequencing: when not already in Transactions view we switch first and bail;
	// the effect re-runs once `viewMode` flips to "transactions" and then sets
	// the query filter.
	// The Balances→Transactions switch also remounts MaximusTableReusable
	// (key={viewMode}) so it re-seeds its visible filter from
	// `tableInitialColumnFilters` automatically.
	//
	// We track the last-applied seed by reference so we only force Transactions
	// view + re-apply the query filter when the seed *itself* changes. Without
	// this, the effect would fight the user: any manual switch back to Balances
	// (while a `dateFilter` is still set) would immediately snap back to
	// Transactions.
	const appliedSeedRef = useRef<ColumnFilters | undefined>(undefined);
	useEffect(() => {
		if (!navigationColumnFilters) {
			appliedSeedRef.current = undefined;
			return;
		}
		if (appliedSeedRef.current === navigationColumnFilters) return;
		if (viewMode !== "transactions") {
			setViewMode("transactions");
			setRows([]);
			setSortParams(TRANSACTION_DEFAULT_SORT);
			resetPaging();
			return;
		}
		const apiFilter = buildApiFilter(
			navigationColumnFilters,
			TRANSACTION_TEXT_FIELDS,
			TRANSACTION_DATE_FIELDS,
			TRANSACTION_NUMBER_FIELDS,
		);
		setColumnFilterParams(apiFilter);
		resetPaging();
		appliedSeedRef.current = navigationColumnFilters;
		// A navigation seed overrides the quick filter, so no button is teal.
		setActiveQuickFilter(null);
	}, [navigationColumnFilters, viewMode, resetPaging]);

	const handleSortChange = useCallback(
		(column: string | null, direction: "asc" | "desc" | null) => {
			const sortMap = isTransactions
				? TRANSACTION_SORT_FIELD_MAP
				: BALANCE_SORT_FIELD_MAP;
			if (!column || !direction || !sortMap[column]) {
				setSortParams(undefined);
			} else {
				setSortParams([
					{
						field: sortMap[column],
						order: direction === "asc" ? "Ascending" : "Descending",
					},
				]);
			}
			resetPaging();
		},
		[isTransactions, resetPaging],
	);

	// ── Export ────────────────────────────────────────────────────────────
	const getExportColumns = useCallback(
		() =>
			isTransactions ? TRANSACTION_EXPORT_COLUMNS : BALANCE_EXPORT_COLUMNS,
		[isTransactions],
	);
	const getExportFilter = useCallback(() => {
		const searchFilter = serverSearchTerm.trim()
			? { summary: serverSearchTerm.trim() }
			: undefined;
		return {
			...source.horizonFilter(viewMode),
			...columnFilterParams,
			...searchFilter,
		};
	}, [viewMode, columnFilterParams, serverSearchTerm, source]);

	const { triggerExport } = useExportToExcel({
		form: isTransactions
			? source.exportTransactionsForm
			: source.exportBalancesForm,
		getColumns: getExportColumns,
		getFilter: getExportFilter,
		fileName: isTransactions
			? source.exportTransactionsFileName
			: source.exportBalancesFileName,
		onFinished: () =>
			toast({
				title: "Export complete",
				description: "Your file has been downloaded.",
			}),
		onError: (msg) =>
			toast({
				title: "Export failed",
				description: msg,
				variant: "destructive",
			}),
	});

	// ── Columns ───────────────────────────────────────────────────────────
	const transactionColumns: ColumnConfig<ForecastRow>[] = useMemo(
		() => [
			{
				key: "forecastDate",
				label: `${getForecastTerm(true)} Date`,
				sortable: true,
				filterable: true,
				filterConfig: { type: "date-range" },
				align: "left",
				width: 140,
				render: (row) => (
					<div className="flex items-center gap-1 text-sm">
						<Calendar className="h-3 w-3 text-muted-foreground" />
						{row.forecastDate ? formatDate(new Date(row.forecastDate)) : "—"}
					</div>
				),
			},
			{
				key: "counterpartyName",
				label: "Counterparty",
				sortable: true,
				filterable: true,
				filterConfig: { type: "text" },
				align: "left",
				width: 180,
				render: (row) => (
					<span className="text-sm">{row.counterpartyName ?? "—"}</span>
				),
			},
			{
				key: "description",
				label: "Description",
				sortable: true,
				filterable: true,
				filterConfig: { type: "text" },
				align: "left",
				width: 240,
				render: (row) => (
					<div className="truncate text-sm">{row.description ?? "—"}</div>
				),
			},
			{
				key: "category",
				label: "Category",
				sortable: true,
				filterable: true,
				filterConfig: {
					type: "select",
					options: categoryOptions.length > 0 ? categoryOptions : [],
				},
				align: "left",
				width: 150,
				render: (row) => <span className="text-sm">{row.category ?? "—"}</span>,
			},
			{
				key: "direction",
				label: "Direction",
				sortable: true,
				filterable: true,
				filterConfig: {
					type: "select",
					options: directionOptions.map((v) => ({
						label: formatLabel(v),
						value: v,
					})),
				},
				align: "left",
				width: 120,
				render: (row) => (
					<span className="text-sm">{formatLabel(row.direction)}</span>
				),
			},
			{
				key: "type",
				label: "Type",
				sortable: true,
				filterable: true,
				filterConfig: {
					type: "select",
					options: typeOptions.map((v) => ({
						label: formatLabel(v),
						value: v,
					})),
				},
				align: "left",
				width: 130,
				render: (row) => (
					<span className="text-sm">{formatLabel(row.type)}</span>
				),
			},
			{
				key: "grossAmount",
				label: "Gross Amount",
				sortable: true,
				filterable: true,
				filterConfig: { type: "number" },
				align: "right",
				width: 130,
				render: (row) => (
					<span
						className={cn(
							"font-semibold text-sm",
							row.direction === "inflow"
								? "text-success-600"
								: "text-destructive",
						)}
					>
						{formatCurrency(row.grossAmount ?? 0, "full")}
					</span>
				),
			},
			{
				key: "outstandingAmount",
				label: "Outstanding",
				sortable: true,
				filterable: true,
				filterConfig: { type: "number" },
				align: "right",
				width: 120,
				render: (row) => (
					<span className="text-sm font-semibold">
						{formatCurrency(row.outstandingAmount ?? 0, "full")}
					</span>
				),
			},
			{
				key: "dueDate",
				label: "Due Date",
				sortable: true,
				filterable: true,
				filterConfig: { type: "date-range" },
				align: "left",
				width: 130,
				render: (row) =>
					row.dueDate ? (
						<div className="flex items-center gap-1 text-sm">
							<Calendar className="h-3 w-3" />
							{formatDate(new Date(row.dueDate))}
						</div>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				key: "layer",
				label: "Layer",
				sortable: true,
				filterable: true,
				filterConfig: {
					type: "select",
					options: layerOptions.length > 0 ? layerOptions : [],
				},
				align: "left",
				width: 130,
				render: (row) => <span className="text-sm">{row.layer ?? "—"}</span>,
			},
			{
				key: "status",
				label: "Status",
				sortable: true,
				filterable: true,
				filterConfig: {
					type: "select",
					options: statusOptions.map((v) => ({
						label: formatLabel(v),
						value: v,
					})),
				},
				align: "left",
				width: 120,
				render: (row) =>
					row.status ? (
						<Badge variant={statusBadgeVariant(row.status)}>
							{formatLabel(row.status)}
						</Badge>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				key: "reference",
				label: "Reference",
				sortable: true,
				filterable: true,
				filterConfig: { type: "text" },
				align: "left",
				width: 130,
				render: (row) => (
					<span className="text-sm">{row.reference ?? "—"}</span>
				),
			},
			{
				key: "dataSourceName",
				label: "Data Source",
				sortable: true,
				filterable: true,
				filterConfig: {
					type: "multi-select",
					options: dataSourceOptions,
				},
				align: "left",
				width: 150,
				render: (row) => (
					<span className="text-sm text-muted-foreground">
						{row.dataSourceName ?? "—"}
					</span>
				),
			},
		],
		[
			categoryOptions,
			directionOptions,
			typeOptions,
			layerOptions,
			statusOptions,
			dataSourceOptions,
			formatCurrency,
			getForecastTerm,
		],
	);

	const balanceColumns: ColumnConfig<ForecastRow>[] = useMemo(
		() => [
			{
				key: "date",
				label: "Date",
				sortable: true,
				filterable: true,
				filterConfig: { type: "date-range" },
				align: "left",
				width: 140,
				render: (row) => (
					<div className="flex items-center gap-1 text-sm">
						<Calendar className="h-3 w-3 text-muted-foreground" />
						{row.date ? formatDate(new Date(row.date)) : "—"}
					</div>
				),
			},
			{
				key: "openingBalance",
				label: "Opening Balance",
				sortable: true,
				filterable: true,
				filterConfig: { type: "number" },
				align: "right",
				width: 140,
				render: (row) => (
					<span className="text-sm font-semibold">
						{formatCurrency(row.openingBalance ?? 0, "full")}
					</span>
				),
			},
			{
				key: "balance",
				label: "Closing Balance",
				sortable: true,
				filterable: true,
				filterConfig: { type: "number" },
				align: "right",
				width: 140,
				render: (row) => (
					<span className="text-sm font-semibold">
						{formatCurrency(row.balance ?? 0, "full")}
					</span>
				),
			},
			{
				key: "incoming",
				label: cashTerms.in,
				sortable: true,
				filterable: true,
				filterConfig: { type: "number" },
				align: "right",
				width: 130,
				render: (row) => (
					<span className="text-sm font-semibold text-success-600">
						{formatCurrency(row.incoming ?? 0, "full")}
					</span>
				),
			},
			{
				key: "outgoing",
				label: cashTerms.out,
				sortable: true,
				filterable: true,
				filterConfig: { type: "number" },
				align: "right",
				width: 130,
				render: (row) => (
					<span className="text-sm font-semibold text-destructive">
						{formatCurrency(row.outgoing ?? 0, "full")}
					</span>
				),
			},
			{
				key: "confidenceScore",
				label: "Confidence",
				sortable: true,
				filterable: true,
				filterConfig: { type: "number" },
				align: "right",
				width: 120,
				render: (row) =>
					row.confidenceScore != null ? (
						<span className="text-sm">{row.confidenceScore}%</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				key: "contributingLayers",
				label: "Contributing Layers",
				sortable: true,
				filterable: true,
				filterConfig: { type: "text" },
				align: "left",
				width: 220,
				render: (row) => (
					<div className="truncate text-sm text-muted-foreground">
						{formatContributingLayers(row.contributingLayers)}
					</div>
				),
			},
		],
		[formatCurrency, cashTerms.in, cashTerms.out],
	);

	const columns = isTransactions ? transactionColumns : balanceColumns;

	// Saved column preferences are keyed by a single screen name shared across
	// both view modes, and they update one render behind the mode toggle. When
	// switching modes the table is remounted (key={viewMode}) and reads its
	// initial visible columns from these preferences — so without this guard it
	// would briefly receive the *previous* mode's column keys, which don't exist
	// in the new mode's column set. That produced an empty column intersection
	// and rendered rows with no cells (the "no data after toggle" bug). Keep only
	// keys valid for the current mode, and fall back to the mode's defaults when
	// none match yet.
	const initialVisibleColumns = useMemo(() => {
		const validKeys = new Set(columns.map((c) => c.key));
		const filtered = savedVisibleColumns.filter((key) => validKeys.has(key));
		if (filtered.length > 0) return filtered;
		return defaultColumns
			.filter((c) => c.defaultVisible)
			.sort((a, b) => a.defaultOrder - b.defaultOrder)
			.map((c) => c.key);
	}, [columns, savedVisibleColumns, defaultColumns]);

	// ── Render ────────────────────────────────────────────────────────────
	return (
		<div className="mt-4">
			{error ? (
				<div className="flex items-center justify-center py-12 text-destructive">
					{error}
				</div>
			) : (
				<div className="flex flex-col">
					<MaximusTableReusable
						key={viewMode}
						data={isDemo ? demoRows : rows}
						columns={columns}
						title={source.title}
						isLoading={isDemo ? false : isLoading}
						minTableHeight={533}
						searchPlaceholder={
							isTransactions
								? "Search by description, counterparty or category..."
								: "Search by contributing layers..."
						}
						enableRowSelection={false}
						enableZebraStriping={true}
						showSearch={true}
						showAdvancedFilter={true}
						initialVisibleColumns={initialVisibleColumns}
						initialColumnFilters={tableInitialColumnFilters}
						onExportToExcel={
							!isDemo && hasPermission("export_data")
								? triggerExport
								: undefined
						}
						onColumnConfigSave={(visibleKeys) => {
							const cols = visibleKeys.map((key, idx) => ({
								key,
								isVisible: true,
								order: idx,
							}));
							const allColumnKeys = columns.map((c) => c.key);
							const hiddenCols = allColumnKeys
								.filter((k) => !visibleKeys.includes(k))
								.map((key, idx) => ({
									key,
									isVisible: false,
									order: visibleKeys.length + idx,
								}));
							saveColumnPreferences([...cols, ...hiddenCols]);
						}}
						initialSortColumn={isDemo && isTransactions ? "forecastDate" : null}
						initialSortDirection={isDemo && isTransactions ? "asc" : null}
						onSortChange={isDemo ? undefined : handleSortChange}
						onFilterChange={handleColumnFilterChange}
						pagination={{
							enabled: true,
							defaultPageSize: 25,
							pageSizeOptions: [25, 50, 100, 200],
							...(isDemo
								? {}
								: {
										totalItems: totalCount,
										controlledPage: serverPage,
										onPageChange: (page: number) => setServerPage(page),
										onPageSizeChange: (newSize: number) => {
											setServerPageSize(newSize);
											resetPaging();
										},
									}),
						}}
						onSearchChange={isDemo ? undefined : handleSearchChange}
						onSearch={
							isDemo
								? (data, query) => {
										const q = query.toLowerCase();
										return data.filter((row) =>
											isTransactions
												? [
														row.description,
														row.counterpartyName,
														row.category,
													].some((v) => v?.toLowerCase().includes(q))
												: (row.contributingLayers ?? "")
														.toLowerCase()
														.includes(q),
										);
									}
								: undefined
						}
						rowIdAccessor={(row) => String(row.rowKey)}
						headerActions={
							<>
								<Button
									variant="outline"
									size="sm"
									onClick={() =>
										handleViewModeChange(
											isTransactions ? "balances" : "transactions",
										)
									}
								>
									<ArrowLeftRight className="h-4 w-4 mr-1" />
									{isTransactions ? "Show Balances" : "Show Transactions"}
								</Button>
								{quickFilterRanges &&
									isTransactions &&
									QUICK_FILTER_KEYS.map((key) => {
										const isActive = activeQuickFilter === key;
										return (
											<Button
												key={key}
												type="button"
												size="sm"
												variant="outline"
												aria-pressed={isActive}
												className={cn(
													isActive &&
														"border-teal-600 bg-teal-600 text-white hover:bg-teal-700 hover:text-white",
												)}
												onClick={() => handleQuickFilterClick(key)}
											>
												{QUICK_FILTER_LABELS[key]}
											</Button>
										);
									})}
							</>
						}
					/>
				</div>
			)}
		</div>
	);
};
